import clsx from "clsx"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { resolveItemImage } from "@/helpers/itemImage"
import { rarityStyle } from "@/helpers/rarity"
import { formatAmount } from "@/helpers/statFormat"
import { showToast } from "@/helpers/toast"
import { generateUuid } from "@/helpers/uuid"
import { useCatalogs } from "@/hooks/useCatalogs"
import {
  buildEffectRows,
  buildStatRows,
  denialReasonKey,
  describeDuration,
  describeRequirement,
  deriveQuickSlotsView,
  extractDenialReason,
  hasUnmetRequirements,
  isBroken,
  itemDetailFlags,
  itemPriceView,
  pickEquipTargetPosition
} from "@/modals/constructor/inventory/InventoryModal/helpers"
import { useModal, useModals } from "@/modals/hooks"
import {
  useAssignQuickSlotMutation,
  useClearQuickSlotMutation,
  useEquipItemMutation,
  useGetInventoryQuery,
  useUnequipItemMutation,
  useUseConsumableMutation
} from "@/store/apis/inventoryAPI"
import { CurrencyIcon } from "@/ui/CurrencyIcon"
import { Modal } from "@/ui/Modal"
import styles from "./InventoryItemModal.module.scss"
import { DurabilityBar } from "./ui/DurabilityBar"
import { SlotPicker } from "./ui/SlotPicker"

const modalType = "inventoryItem"

export const InventoryItemModal: FC = () => {
  const { t, i18n } = useTranslation("inventory")
  const { modal, getModalParams } = useModal(modalType)
  const { openModal } = useModals()
  const instanceId = getModalParams(modalType)?.instanceId

  const { item, worn, quickSlots, consumables, isSuccess } =
    useGetInventoryQuery(undefined, {
      selectFromResult: ({ data, isSuccess }) => ({
        item: data?.items.find((i) => i.id === instanceId) ?? null,
        worn: data?.items.filter((i) => i.slotPosition !== null) ?? [],
        quickSlots: data?.quickSlots ?? [],
        consumables: data?.items.filter((i) => i.kind === "CONSUMABLE") ?? [],
        isSuccess
      })
    })

  const { slotLayout, stats, currencies } = useCatalogs()

  const [equipItem, equipState] = useEquipItemMutation()
  const [unequipItem, unequipState] = useUnequipItemMutation()
  const [triggerUse, consumeState] = useUseConsumableMutation()
  const [assignSlot, assignState] = useAssignQuickSlotMutation()
  const [clearSlot, clearState] = useClearQuickSlotMutation()
  const [pickerOpen, setPickerOpen] = useState(false)
  const busy =
    equipState.isLoading ||
    unequipState.isLoading ||
    consumeState.isLoading ||
    assignState.isLoading ||
    clearState.isLoading

  useEffect(() => {
    if (isSuccess && !item) {
      modal.close()
    }
  }, [isSuccess, item, modal])

  if (!item) {
    return <Modal.Center type={modalType} className={styles.raise} />
  }

  const image = resolveItemImage(item.definitionCode)
  const isEquipment = item.kind === "EQUIPMENT"
  const isWorn = item.slotPosition !== null
  const flags = itemDetailFlags(item)
  const priceView = flags.price
    ? itemPriceView(item.price, currencies, i18n.language)
    : null
  const target =
    isEquipment && !isWorn
      ? pickEquipTargetPosition(item, slotLayout, worn)
      : null
  const isConsumable = item.consumable !== null
  const beltSlots = quickSlots.reduce<number[]>(
    (indexes, slot, index) =>
      slot?.definitionCode === item.definitionCode
        ? [...indexes, index]
        : indexes,
    []
  )
  const durationText =
    item.consumable && item.consumable.durationSeconds !== null
      ? describeDuration(item.consumable.durationSeconds)
          .map((part) => `${part.value} ${t(`common:duration.${part.unit}`)}`)
          .join(" ")
      : null

  const handleEquip = async () => {
    if (!target) {
      return
    }
    try {
      const res = await equipItem({
        instanceId: item.id,
        position: target
      }).unwrap()
      if (res.displaced) {
        showToast.info(
          t("toast.returnedToBag", {
            name: t(`item.${res.displaced.definitionCode}.name`)
          })
        )
      }
      modal.close()
    } catch (error) {
      showToast.error(t(denialReasonKey(extractDenialReason(error))))
    }
  }

  const handleUnequip = async () => {
    try {
      await unequipItem({ instanceId: item.id }).unwrap()
      modal.close()
    } catch (error) {
      showToast.error(t(denialReasonKey(extractDenialReason(error))))
    }
  }

  const handleUse = async () => {
    try {
      await triggerUse({
        definitionCode: item.definitionCode,
        idempotencyKey: generateUuid()
      }).unwrap()
      showToast.success(
        t("toast.used", { name: t(`item.${item.definitionCode}.name`) })
      )
    } catch (error) {
      showToast.error(t(denialReasonKey(extractDenialReason(error))))
    }
  }

  const handleAssign = async (slotIndex: number) => {
    try {
      await assignSlot({
        slotIndex,
        definitionCode: item.definitionCode
      }).unwrap()
      setPickerOpen(false)
    } catch (error) {
      showToast.error(t(denialReasonKey(extractDenialReason(error))))
    }
  }

  const handleRemoveFromBelt = async () => {
    try {
      await Promise.all(
        beltSlots.map((slotIndex) => clearSlot({ slotIndex }).unwrap())
      )
    } catch (error) {
      showToast.error(t(denialReasonKey(extractDenialReason(error))))
    }
  }

  return (
    <Modal.Center type={modalType} className={styles.raise}>
      <div className={styles.InventoryItemModal}>
        {image ? (
          <img className={styles.InventoryItemModal_icon} src={image} alt="" />
        ) : (
          <div className={styles.InventoryItemModal_icon} />
        )}
        <h3 className={styles.InventoryItemModal_name}>
          {t(`item.${item.definitionCode}.name`)}
        </h3>
        <div className={styles.InventoryItemModal_meta}>
          <span>{t(`kind.${item.kind.toLowerCase()}`)}</span>
          {item.quantity > 1 && <span>×{item.quantity}</span>}
        </div>
        {item.inactive && (
          <p className={styles.InventoryItemModal_inactive}>
            {t("detail.inactive")}
          </p>
        )}
        {isBroken(item) && (
          <p className={styles.InventoryItemModal_broken}>
            {t("detail.broken")}
          </p>
        )}
        <div className={styles.InventoryItemModal_details}>
          {flags.rarity && (
            <div className={styles.InventoryItemModal_row}>
              <span>{t("detail.rarity")}</span>
              <span
                className={styles.InventoryItemModal_rarityValue}
                style={rarityStyle(item)}
              >
                {t(`catalog:rarity.${item.rarityCode}`, {
                  defaultValue: item.rarityCode
                })}
              </span>
            </div>
          )}
          {flags.level && (
            <div className={styles.InventoryItemModal_row}>
              <span>{t("detail.level")}</span>
              <span>{item.level}</span>
            </div>
          )}
          {flags.stats && (
            <>
              <h4 className={styles.InventoryItemModal_sectionTitle}>
                {t("detail.stats")}
              </h4>
              {buildStatRows(item.stats, stats).map((row) => (
                <div key={row.code} className={styles.InventoryItemModal_row}>
                  <span>
                    {t(`catalog:stat.${row.code}`, { defaultValue: row.code })}
                  </span>
                  <span
                    className={clsx(styles.InventoryItemModal_statValue, {
                      [styles.negative]: row.kind === "single" && row.value < 0
                    })}
                  >
                    {row.kind === "range" ? row.formatted : row.formatted.text}
                    {row.kind === "single" &&
                      row.formatted.note === "points" &&
                      ` ${t("unit.points")}`}
                  </span>
                </div>
              ))}
            </>
          )}
          {flags.effects && item.consumable && (
            <>
              <h4 className={styles.InventoryItemModal_sectionTitle}>
                {t("detail.effects")}
              </h4>
              {buildEffectRows(item.consumable, stats).map((row) => (
                <div
                  key={
                    row.kind === "instant"
                      ? `instant:${row.vitalCode}`
                      : `duration:${row.statCode}`
                  }
                  className={styles.InventoryItemModal_row}
                >
                  <span>
                    {row.kind === "instant"
                      ? t(`catalog:vital.${row.vitalCode}`, {
                          defaultValue: row.vitalCode
                        })
                      : t(`catalog:stat.${row.statCode}`, {
                          defaultValue: row.statCode
                        })}
                  </span>
                  <span
                    className={clsx(styles.InventoryItemModal_statValue, {
                      [styles.negative]: row.value < 0
                    })}
                  >
                    {row.kind === "instant"
                      ? row.formatted
                      : row.formatted.text}
                    {row.kind === "duration" &&
                      row.formatted.note === "points" &&
                      ` ${t("unit.points")}`}
                  </span>
                </div>
              ))}
              {durationText && (
                <div className={styles.InventoryItemModal_row}>
                  <span>{t("detail.duration")}</span>
                  <span>{durationText}</span>
                </div>
              )}
            </>
          )}
          {flags.requirements && (
            <>
              <h4 className={styles.InventoryItemModal_sectionTitle}>
                {t("detail.requirements")}
              </h4>
              {item.requirements.map((req, index) => {
                const descriptor = describeRequirement(req)
                const valueClass = clsx(styles.InventoryItemModal_reqValue, {
                  [styles.met]: req.met,
                  [styles.unmet]: !req.met
                })
                return (
                  <div key={index} className={styles.InventoryItemModal_row}>
                    {descriptor.type === "level" && (
                      <>
                        <span>{t("requirement.level")}</span>
                        <span className={valueClass}>{descriptor.value}</span>
                      </>
                    )}
                    {descriptor.type === "archetype" && (
                      <>
                        <span>{t("requirement.archetype")}</span>
                        <span className={valueClass}>
                          {t(`catalog:archetype.${descriptor.targetKey}`, {
                            defaultValue: descriptor.targetKey
                          })}
                        </span>
                      </>
                    )}
                    {descriptor.type === "stat" && (
                      <>
                        <span>
                          {t(`catalog:stat.${descriptor.targetKey}`, {
                            defaultValue: descriptor.targetKey
                          })}
                        </span>
                        <span className={valueClass}>
                          {descriptor.currentValue == null
                            ? formatAmount(descriptor.minValue)
                            : `${formatAmount(descriptor.currentValue)} / ${formatAmount(descriptor.minValue)}`}
                        </span>
                      </>
                    )}
                    {descriptor.type === "unknown" && (
                      <span className={valueClass}>
                        {t("requirement.unknown")}
                      </span>
                    )}
                  </div>
                )
              })}
            </>
          )}
          <div className={styles.InventoryItemModal_row}>
            <span>{t("detail.unitWeight")}</span>
            <span>{formatAmount(item.unitWeight)}</span>
          </div>
          {flags.totalWeight && (
            <div className={styles.InventoryItemModal_row}>
              <span>{t("detail.totalWeight")}</span>
              <span>{formatAmount(item.stackWeight)}</span>
            </div>
          )}
          {priceView !== null && (
            <div className={styles.InventoryItemModal_row}>
              <span>{t("detail.price")}</span>
              <span className={styles.InventoryItemModal_price}>
                {priceView.amountText}
                <CurrencyIcon
                  size="small"
                  iconUrl={priceView.iconUrl}
                  alt={t(`catalog:currency.${item.price.currencyCode}.name`)}
                />
              </span>
            </div>
          )}
          {flags.durability && (
            <div className={styles.InventoryItemModal_durability}>
              <span>{t("detail.durability")}</span>
              <DurabilityBar
                current={item.durability ?? 0}
                max={item.maxDurability ?? 0}
              />
            </div>
          )}
          {i18n.exists(`item.${item.definitionCode}.description`, {
            ns: "inventory"
          }) && (
            <p className={styles.InventoryItemModal_description}>
              {t(`item.${item.definitionCode}.description`)}
            </p>
          )}
        </div>
        <div className={styles.InventoryItemModal_actions}>
          <button type="button" onClick={modal.close}>
            {t("action.close")}
          </button>
          {isEquipment && isWorn && (
            <button type="button" disabled={busy} onClick={handleUnequip}>
              {t("action.unequip")}
            </button>
          )}
          {isEquipment && !isWorn && (
            <button
              type="button"
              disabled={
                busy || !target || hasUnmetRequirements(item) || isBroken(item)
              }
              onClick={handleEquip}
            >
              {t("action.equip")}
            </button>
          )}
          {isConsumable && (
            <button type="button" disabled={busy} onClick={handleUse}>
              {t("action.use")}
            </button>
          )}
          {isConsumable && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setPickerOpen((open) => !open)}
            >
              {t("action.assignToBelt")}
            </button>
          )}
          {isConsumable && beltSlots.length > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={handleRemoveFromBelt}
            >
              {t("belt.remove")}
            </button>
          )}
          {item.destroyable && (
            <button
              type="button"
              disabled={busy || isWorn}
              onClick={() =>
                openModal("inventoryDiscardConfirm", { instanceId: item.id })
              }
            >
              {t("action.discard")}
            </button>
          )}
        </div>
        {isConsumable && pickerOpen && (
          <SlotPicker
            view={deriveQuickSlotsView(quickSlots, consumables)}
            busy={assignState.isLoading}
            onPick={handleAssign}
          />
        )}
        {item.destroyable && isWorn && (
          <p className={styles.InventoryItemModal_hint}>
            {t("discard.unequipHint")}
          </p>
        )}
      </div>
    </Modal.Center>
  )
}
